import { Inject, Injectable, InjectionToken, OnDestroy, Optional } from '@angular/core';
import { ContactsRefreshSignal, DataService } from '@app/shared/common/auth/data.service';
import {
    ChangeLeadStageFromTaskInput,
    ChangeLeadStageFromTaskResultDtoIHandleError,
    ClientStageTransitionDto,
    ContactServiceProxy,
    IClientStageDto,
    LeadStageChangeSource,
    SetLostReasonInput,
    TaskLeadStageServiceProxy,
    UndoWonInput,
    UndoWonResultDtoIHandleError,
} from '@shared/service-proxies/service-proxies';
import { EMPTY, Observable, ReplaySubject, Subject, defer, merge, of } from 'rxjs';
import {
    catchError,
    defaultIfEmpty,
    distinctUntilChanged,
    filter,
    map,
    share,
    shareReplay,
    startWith,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

import { transitionCompletedTask } from '../client-stage-commit.service';
import { LeadStageConfig, StageCategory } from '../task-action/task-action-context.models';
import {
    ChangeLeadStageRequest,
    LeadFollowUpRequest,
    LeadFollowUpSource,
    LeadFollowUpTransitionLike,
    LeadStageChangeResult,
    LeadStageChangedEvent,
    LeadStageOrigin,
    LeadTriggerBox,
    LostReasonSaveResult,
    WonUndoResult,
} from './lead-lifecycle.models';
import { isLostReasonCode } from './lost-reason/lost-reasons';
import { LostReasonRequest, NO_LOST_REASON, findLostReason, lostReasonRequest } from './lost-reason/lost-reason-catalog';
import { LostReasonCatalogService } from './lost-reason/lost-reason-catalog.service';
import { AppConsts } from '@shared/commonHelper/AppConsts';

/**
 * The slice of a TaskLeadStage answer `setLostReason` reads (every generated `…IHandleError` has it). Only `isError` and
 * `errorMessage`: the answer's `responseData` is not part of the contract (BE-67).
 */
interface TaskLeadStageAnswer {
    readonly isError?: boolean | null;
    readonly errorMessage?: string | null;
}

/** The slice of the server's stage transition `setLostReason` reads. */
export type LostTransitionLike = Pick<ClientStageTransitionDto, 'isTransition' | 'transitionId' | 'toStageAct'>;

/**
 * The slice of a stage move `announceStageTransitions` reads: a `ClientStageTransitionDto` has it, and so does the board's Undo
 * (`undoneMoveOf`, BRDX-65). `toStageAct` is SrcClientStage.Act (0 Open, 1 Won, 2 Lost; none = Open).
 */
export interface LeadStageMoveLike {
    readonly isTransition?: boolean | null;
    readonly clientId?: number | null;
    readonly fromStageId?: number | null;
    readonly toStageId?: number | null;
    readonly toStageAct?: number | null;
    readonly transitionId?: number | null;
    /** BRDX-67: what the move's own answer said about the AI next action (the board's Undo: its card's wait); absent = not known. */
    readonly nextActionPending?: boolean | null;
}

/** BRDX-67: the event's `nextActionPending` member - only when the move's answer said (a boolean), so events stay as they were. */
function nextActionPendingOf(value: boolean | null | undefined): { nextActionPending?: boolean } {
    return typeof value === 'boolean' ? { nextActionPending: value } : {};
}

/** How long a loaded stage list counts as fresh when a task panel opens (B.12: "TTL 5 min on store open()"). */
export const LEAD_STAGE_CONFIG_TTL_MS = 5 * 60 * 1000;

/** The members of a SignalR `getClientUpdate` push the Lead surfaces read (`ClientUpdateSignal` has them). */
export interface LeadPushSignal {
    readonly entityId?: number | null;
    readonly entityIds?: readonly number[] | null;
}

/**
 * Lead Pipeline LP9: the two Lead pushes of `UserSignalrService` - `stages$` (`leadStages`: stage settings changed, STAGE ids)
 * and `summaries$` (`leadSummary`: a lead's Living Lead Summary or AI next action changed, PEOPLE ids). A token AppModule
 * provides from the hub service, so this file and its specs never load the hub (none provided: no pushes, as before).
 */
export interface LeadPipelinePushes {
    readonly stages$: Observable<LeadPushSignal>;
    readonly summaries$: Observable<LeadPushSignal>;
    /** Configurable lost reasons (`lostReasons`, REASON ids): read by `LostReasonCatalogService`; optional for older providers. */
    readonly lostReasons$?: Observable<LeadPushSignal>;
}

export const LEAD_PIPELINE_PUSHES = new InjectionToken<LeadPipelinePushes>('LEAD_PIPELINE_PUSHES');

/**
 * LP9 (PER-51, user 2026-10-21 "update task panel straight away"): client stage settings changed. `local` = this tab's
 * settings signal (the "Types, stages & more" window), which names no stage; `push` = another tab or user (the server's
 * `leadStages` push, after the save commits).
 */
export interface LeadStageSettingsChange {
    readonly source: 'local' | 'push';
    /** The stages named; null when not known (a local save), so every stage counts as touched. */
    readonly stageIds: readonly number[] | null;
}

/** The record ids a push names (`entityIds`, else `entityId`), positive and distinct. */
export function pushIdsOf(signal: LeadPushSignal | null | undefined): number[] {
    if (!signal) {
        return [];
    }
    const ids = signal.entityIds && signal.entityIds.length ? signal.entityIds : [signal.entityId];
    const out: number[] = [];
    ids.forEach((id) => {
        const n = Number(id);
        if (n > 0 && out.indexOf(n) === -1) {
            out.push(n);
        }
    });
    return out;
}

/**
 * Whether a stage-settings change can touch a lead whose stage is `stageId` (U-PER-28, U-PER-29): it names that stage, it
 * names none (a local save), or the lead's stage is not known yet. A change naming only other stages does not.
 */
export function stageSettingsChangeTouches(change: LeadStageSettingsChange | null | undefined, stageId: number | null | undefined): boolean {
    if (!change) {
        return false;
    }
    const id = idOrNull(stageId);
    return change.stageIds === null || id === null || change.stageIds.indexOf(id) !== -1;
}

/** The pushes follow the Lead Pipeline flag, read when one arrives (the server sends none while its flag is off). */
function leadPushesEnabled(): boolean {
    return !!AppConsts.leadPipelineFeatureEnabled;
}

/** SrcClientStage.Act -> category: null or 0 open, 1 won, 2 lost. Anything else counts as open. */
export function leadStageCategoryOf(act: number | null | undefined): StageCategory {
    switch (act) {
        case 1:
            return 'won';
        case 2:
            return 'lost';
        default:
            return 'open';
    }
}

/**
 * `Contact/GetAllClientStages` rows -> the tenant's stage list in configured order (`sortOrder`, the TblId 17 rank,
 * then id). ALL stages: Lost and inactive ones included (the Stage column must name a Lost or deactivated stage a
 * person is still in; the rail filters its own, B.6). Rows without an id are dropped. `nextStep*`: the stage's Next step
 * (Lead Pipeline LP1, D4) - Open stages only, and null while the server's flag is off.
 */
export function toLeadStageConfigs(
    stages: readonly (Partial<IClientStageDto> | null | undefined)[] | null | undefined
): readonly LeadStageConfig[] {
    const list: LeadStageConfig[] = [];
    (stages || []).forEach((dto) => {
        if (!dto || typeof dto.id !== 'number') {
            return;
        }
        const category = leadStageCategoryOf(dto.act);
        const nextStep = category === 'open' && typeof dto.nextStep === 'string' && dto.nextStep.trim() ? dto.nextStep : null;
        list.push({
            id: dto.id,
            name: typeof dto.stage === 'string' ? dto.stage : '',
            category,
            sortOrder: typeof dto.sortOrder === 'number' ? dto.sortOrder : Number.MAX_SAFE_INTEGER,
            isActive: dto.isActive !== false,
            suggestFollowUpDays: typeof dto.suggestFollowUpDays === 'number' ? dto.suggestFollowUpDays : null,
            nextStep,
            nextStepNotes: nextStep && typeof dto.nextStepNotes === 'string' && dto.nextStepNotes.trim() ? dto.nextStepNotes : null,
        });
    });
    return list.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
}

/**
 * A `refreshContacts` value that may mean "the client settings changed": the legacy many-records values (`false`,
 * `true`, `null`), which the "Types, stages & more" window sends after every save (client-settings.service.ts
 * `notifyChanged`). One person's or company's record change, or a drawer closing (a string), never changes the stages.
 */
export function isStageSettingsSignal(signal: ContactsRefreshSignal): boolean {
    return signal === false || signal === true || signal === null || signal === undefined;
}

/**
 * The task stage facade's `source` for a task-surface origin (the server's `LeadStageChangeSource`: 1 TaskProgressRail,
 * 2 LeadNextSteps, 3 LeadReminderFlow); null for the LP surfaces, which have no transport until P6 (FE-122).
 */
export function taskSurfaceSourceOf(origin: LeadStageOrigin | null | undefined): LeadStageChangeSource | null {
    switch (origin) {
        case 'task_progress_rail':
            return LeadStageChangeSource._1;
        case 'lead_next_steps':
            return LeadStageChangeSource._2;
        case 'lead_reminder_flow':
            return LeadStageChangeSource._3;
        default:
            return null;
    }
}

/** A failed or unsent stage write: never an error notification, the caller words it (S-43). */
export function failedLeadStageChange(errorMessage: string | null): LeadStageChangeResult {
    return { ok: false, errorMessage, transition: null, facts: null, contextTaskCompleted: false };
}

/**
 * A drafted stage change is a MOVE to a Lost-category stage (FE-140, the Edit-relationship popover's `Why?` block): a
 * stage is drafted, it is not the current one, and its act is Lost (2). A Lost stage the person is already in is no
 * move: no transition would carry a reason.
 */
export function isLostStageMove(
    currentStageId: number | null | undefined,
    draftStageId: number | null | undefined,
    draftStageAct: number | null | undefined
): boolean {
    const draft = idOrNull(draftStageId);
    return draft !== null && draft !== idOrNull(currentStageId) && leadStageCategoryOf(draftStageAct) === 'lost';
}

/**
 * The transition id a lost reason can be written to (FE-140): a real move (`isTransition`) to a Lost-category stage
 * that carries its id; null for anything else (an Open or Won move, a same-stage answer, a missing transition).
 */
export function lostTransitionIdOf(transition: LostTransitionLike | null | undefined): number | null {
    if (!transition || transition.isTransition !== true || leadStageCategoryOf(transition.toStageAct) !== 'lost') {
        return null;
    }
    return idOrNull(transition.transitionId);
}

/**
 * `ChangeLeadStageFromTask`'s answer as a `LeadStageChangeResult`. `isError` is a refusal (its sentence kept, e.g. "This
 * task is no longer the lead’s primary task."); a success carries the transition and the task's refreshed facts. A
 * success without a body is a failure with no message. `contextTaskCompleted` is the ONE cross-layer test
 * (`transitionCompletedTask`): a Won / Lost move ended and completed this very task.
 */
export function toLeadStageChangeResult(
    res: ChangeLeadStageFromTaskResultDtoIHandleError | null | undefined,
    contextTaskId: number | null | undefined
): LeadStageChangeResult {
    const data = res && !res.isError ? res.responseData : undefined;
    if (!data) {
        return failedLeadStageChange(res ? textOrNull(res.errorMessage) : null);
    }
    const transition = data.transition || null;
    return {
        ok: true,
        errorMessage: null,
        transition,
        facts: data.facts || null,
        contextTaskCompleted: transitionCompletedTask(transition || undefined, idOrNull(contextTaskId) || undefined),
        followUpDate: (transition && transition.followUpDate) || null,
        followUpSource: followUpSourceOf(transition),
    };
}

/**
 * Lead Pipeline LP2: where the Primary's date after a move came from - the stage rule (scheduled), none (a No-follow-up stage
 * completed or left it), or kept (an unanswered reply kept the task's dates, TQ2). Undefined when the rule did not run.
 */
export function followUpSourceOf(transition: ClientStageTransitionDto | null | undefined): LeadFollowUpSource | undefined {
    switch (transition ? transition.followUpResult : null) {
        case 'scheduled':
            return 'stage';
        case 'completed':
        case 'left_completed':
            return 'none';
        case 'reply_kept':
        case 'reopened':
            return 'kept';
        default:
            return undefined;
    }
}

/**
 * The FE lead-stage facade (MYDAY_LEAD_COMMS_PLAN.md Appendix B.12; FE-11 for P2). P2 serves the ONE stage list every
 * Stage surface reads (My Day's Stage column now; the rail, `→ {next}` and Lead Next Steps' chips from P3) and keeps
 * it fresh: a rename, reorder, add or delete in Settings shows without a page reload (§43, §96).
 *
 * - `stageConfig$`: the tenant's stages in configured order, cached and shared (`shareReplay(1)`); loaded on the first
 *   subscription, reloaded on the settings save signal and on `refreshStageConfig()`. An error keeps the last good
 *   list (no emission); an identical reload emits nothing.
 * - `stageChanged$`: one person's stage moved through this facade: `changeStage` (FE-14, P3) feeds it, LP's tenant
 *   event joins in P6 (FE-122).
 * - `changeStage` (FE-14, P3): the ONE FE stage writer of this epic's task surfaces (the rail; Lead Next Steps and the
 *   Lead Reminder flow in P4), over the task stage facade `TaskLeadStage/ChangeLeadStageFromTask` (BE-40). A move to a
 *   Lost stage may carry an optional lost reason (handoff 5, PR22; BE-66).
 * - `setLostReason` (FE-140): writes a lost reason onto a Lost transition another save just made (the task panel's
 *   Edit-relationship popover saves through ContactAppService, which takes none), over `TaskLeadStage/SetLostReason`
 *   (BE-67).
 *
 * Root-provided, no new DataService channel (STANDARDS §1.12): it listens on the EXISTING `refreshContacts` and, after
 * a stage move, sends the EXISTING one-record signal on it. LP9: the tenant-scoped SignalR pushes arrive through the
 * `LEAD_PIPELINE_PUSHES` token (`stageSettingsChanged$`, `leadSummaryChanged$`).
 * Name and folder match the Lead Pipeline peer's proposal, so LP extends this file instead of adding a second facade.
 */
@Injectable({ providedIn: 'root' })
export class LeadLifecycleClientService implements OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private readonly reload$ = new Subject<void>();
    private readonly stageChangedSource = new Subject<LeadStageChangedEvent>();
    private readonly followUpRequestSource = new Subject<LeadFollowUpRequest>();
    /** LP4 (LC-07): how many "Follow up on" hosts listen (MainLayout mounts one); none = the task panel instead. */
    private followUpHosts = 0;
    /** When the last stage-list request went out (epoch ms); null before the first. Feeds the open-time TTL. */
    private lastLoadStartedAtMs: number | null = null;

    readonly stageConfig$: Observable<readonly LeadStageConfig[]>;
    readonly stageChanged$: Observable<LeadStageChangedEvent> = this.stageChangedSource.asObservable();
    /** LP4 (LC-07): the lead toast's `Change` requests, for the shared "Follow up on" host (`vf-lead-follow-up-host`). */
    readonly followUpRequests$: Observable<LeadFollowUpRequest> = this.followUpRequestSource.asObservable();
    /**
     * LP9 (PER-51): client stage settings changed - this tab's settings signal, or (Lead Pipeline on) the `leadStages` push of
     * another tab or user. The stage list reloads on every one, at once (the 5-minute TTL never holds a push back); an open
     * task panel re-reads its facts when the change can touch its lead (`stageSettingsChangeTouches`).
     */
    readonly stageSettingsChanged$: Observable<LeadStageSettingsChange>;
    /** LP9: (Lead Pipeline on) the people whose Living Lead Summary or AI next action the server just changed (`leadSummary`). */
    readonly leadSummaryChanged$: Observable<readonly number[]>;

    constructor(
        private readonly contactService: ContactServiceProxy,
        private readonly dataService: DataService,
        private readonly taskLeadStageService: TaskLeadStageServiceProxy,
        @Optional() @Inject(LEAD_PIPELINE_PUSHES) pushes?: LeadPipelinePushes | null,
        @Optional() private readonly lostReasons?: LostReasonCatalogService | null
    ) {
        this.stageConfig$ = this.reload$.pipe(
            startWith(undefined as void),
            switchMap(() => this.loadStages()),
            filter((stages): stages is readonly LeadStageConfig[] => stages !== null),
            distinctUntilChanged(sameStageConfigs),
            takeUntil(this.destroy$),
            shareReplay(1)
        );

        const local$ = this.dataService.refreshContacts.pipe(
            filter(isStageSettingsSignal),
            map((): LeadStageSettingsChange => ({ source: 'local', stageIds: null }))
        );
        const pushed$ = pushes && pushes.stages$
            ? pushes.stages$.pipe(
                  filter(() => leadPushesEnabled()),
                  map((signal): LeadStageSettingsChange => {
                      const ids = pushIdsOf(signal);
                      return { source: 'push', stageIds: ids.length ? ids : null };
                  })
              )
            : EMPTY;
        this.stageSettingsChanged$ = merge(local$, pushed$).pipe(takeUntil(this.destroy$), share());
        this.leadSummaryChanged$ = pushes && pushes.summaries$
            ? pushes.summaries$.pipe(
                  filter(() => leadPushesEnabled()),
                  map(pushIdsOf),
                  filter((ids) => ids.length > 0),
                  takeUntil(this.destroy$),
                  share()
              )
            : EMPTY;

        this.stageSettingsChanged$.subscribe(() => this.refreshStageConfig());
    }

    /**
     * Lead Pipeline LP2: the lead toast's `Change` - opens the lead's follow-up task (its date and follow-up rows) in the task
     * panel, over the EXISTING `openEditTaskModal` channel (no new DataService channel, STANDARDS §1.12).
     */
    openLeadTask(taskId: number | null | undefined): void {
        const id = idOrNull(taskId);
        if (id !== null) {
            this.dataService.openEditTaskModal.next([{ id }, false, false]);
        }
    }

    /**
     * Lead Pipeline LP4 (LC-07): the lead toast's `Change` - "Follow up on" (Today, Tomorrow, Stage default, Next week, Pick a
     * date) for the lead's follow-up task, over the shared host MainLayout mounts. Flag off, no host, or a transition without
     * its task or person: today's behaviour, the task panel (`openLeadTask`). `anchor` is the clicked trigger's box: the
     * picker opens at it (2026-10-23; without one it keeps the old spot above the toast).
     */
    changeLeadFollowUp(transition: LeadFollowUpTransitionLike | null | undefined, personName?: string | null, anchor?: LeadTriggerBox | null): void {
        const taskId = idOrNull(transition ? transition.primaryTaskId : null);
        const personId = idOrNull(transition ? transition.clientId : null);
        if (!AppConsts.leadPipelineFeatureEnabled || this.followUpHosts === 0 || taskId === null || personId === null) {
            this.openLeadTask(taskId);
            return;
        }
        this.followUpRequestSource.next({
            personId,
            taskId,
            stageId: idOrNull(transition ? transition.toStageId : null),
            personName: personName || null,
            anchor: anchor ? { top: anchor.top, bottom: anchor.bottom, left: anchor.left, right: anchor.right } : null,
        });
    }

    /**
     * Lead Pipeline LP5: a stage move another surface made through its own endpoint (the person window's Lead journey banner,
     * `LeadPipeline/MoveCard` with origin `person_journey`), fanned out like a facade move - one `stageChanged$` (the open
     * drawer's Stage chip, a task panel's rail) and one person-record `refreshContacts` (this tab's grids and board). No new
     * DataService channel (STANDARDS §1.12).
     */
    announcePersonMove(move: {
        readonly personId: number;
        readonly fromStageId: number | null;
        readonly toStageId: number;
        readonly toStageAct: number | null | undefined;
        readonly transitionId: number | null;
        readonly origin: LeadStageOrigin;
        /** BRDX-67: what the move's answer said about the AI next action (the event's `nextActionPending`); left out = not known. */
        readonly nextActionPending?: boolean | null;
    }): void {
        const personId = idOrNull(move ? move.personId : null);
        const toStageId = idOrNull(move ? move.toStageId : null);
        if (personId === null || toStageId === null) {
            return;
        }
        this.stageChangedSource.next({
            personId,
            fromStageId: idOrNull(move.fromStageId),
            toStageId,
            toCategory: leadStageCategoryOf(move.toStageAct),
            origin: move.origin,
            contextTaskId: null,
            transitionId: idOrNull(move.transitionId),
            source: 'local',
            ...nextActionPendingOf(move.nextActionPending),
        });
        this.dataService.refreshContacts.next({ kind: 'person', id: personId });
    }

    /**
     * BRDX-64 (user 2026-10-23): stage moves a surface made through its own endpoint and already announced on `refreshContacts`
     * (the board's `MoveCard`, the task panel's Edit relationship and composer saves, the person window's Stage field) - one
     * `stageChanged$` per real person move, so this tab's other surfaces show it at once (a task panel's banner, the person
     * window's banner and Stage field, the board, My Day's rows), `Generating next action…` included where the server starts one.
     * No `refreshContacts` here. Nothing for a same-stage answer or a transition without its person or stage.
     */
    announceStageTransitions(
        transitions: ReadonlyArray<LeadStageMoveLike | null | undefined> | null | undefined,
        origin: LeadStageOrigin,
        contextTaskId: number | null = null
    ): void {
        (transitions || []).forEach((transition) => {
            const personId = transition && transition.isTransition === true ? idOrNull(transition.clientId) : null;
            const toStageId = personId !== null ? idOrNull(transition.toStageId) : null;
            if (personId === null || toStageId === null) {
                return;
            }
            this.stageChangedSource.next({
                personId,
                fromStageId: idOrNull(transition.fromStageId),
                toStageId,
                toCategory: leadStageCategoryOf(transition.toStageAct),
                origin,
                contextTaskId: idOrNull(contextTaskId),
                transitionId: idOrNull(transition.transitionId),
                source: 'local',
                ...nextActionPendingOf(transition.nextActionPending),
            });
        });
    }

    /** LP4: the "Follow up on" host announces itself while mounted; the returned function signs it off. */
    registerFollowUpHost(): () => void {
        this.followUpHosts++;
        let done = false;
        return () => {
            if (!done) {
                done = true;
                this.followUpHosts--;
            }
        };
    }

    /** Reload the stage list now; a newer request replaces one in flight. What a Stage cell's unknown id asks for. */
    refreshStageConfig(): void {
        this.reload$.next();
    }

    /**
     * The open-time TTL (B.12, FE-11): reload the stage list when the last request went out `maxAgeMs` ago or more, so
     * another user's or tab's Settings change reaches a task panel on its next open (same-tab saves already reload on
     * the settings signal). Before the first request it is a plain `refreshStageConfig()`. With the Lead Pipeline on, LP9's
     * `leadStages` push reloads it at once (`stageSettingsChanged$`); the TTL stays as the fallback for a missed push.
     */
    refreshStageConfigIfStale(maxAgeMs: number = LEAD_STAGE_CONFIG_TTL_MS): void {
        if (this.lastLoadStartedAtMs !== null && Date.now() - this.lastLoadStartedAtMs < maxAgeMs) {
            return;
        }
        this.refreshStageConfig();
    }

    /**
     * Moves a lead's stage from a task surface (FE-14; B.11 step 2): ONE `TaskLeadStage/ChangeLeadStageFromTask` with
     * `{ taskId: contextTaskId, personId, stageId: toStageId, source }`. The write starts at once and runs to its end
     * even if the caller unsubscribes (a closed panel must not cancel a move the server may already have made); the
     * answer is replayed to every subscriber. It never errors: a refusal is `ok: false` with the server's sentence, a
     * transport failure `ok: false` with no message (the app's HTTP interceptor has already reported it).
     *
     * After a REAL move (`transition.isTransition`) it emits one `stageChanged$` (source 'local') and one EXISTING
     * `refreshContacts` person record, so every Stage surface of this tab re-reads that person (§40.1, §94). A same-stage
     * answer, a refusal and a failure emit nothing. An origin without a task transport (the LP surfaces until P6) or a
     * request without a task, person or stage sends nothing and answers `ok: false`. `lostReason` goes out only when it
     * is one of the six codes (handoff 5, PR22); the server ignores it unless the stage is Lost-category (BE-66).
     */
    changeStage(req: ChangeLeadStageRequest): Observable<LeadStageChangeResult> {
        const source = req ? taskSurfaceSourceOf(req.origin) : null;
        const taskId = req ? idOrNull(req.contextTaskId) : null;
        const personId = req ? idOrNull(req.personId) : null;
        const stageId = req ? idOrNull(req.toStageId) : null;
        if (source === null || taskId === null || personId === null || stageId === null) {
            return of(failedLeadStageChange(null));
        }
        const reason = this.lostReasonRequestOf(req.lostReason, req.lostReasonNote);
        const input = new ChangeLeadStageFromTaskInput({
            taskId,
            personId,
            stageId,
            source,
            lostReason: reason.lostReason,
            lostReasonId: reason.lostReasonId,
            lostReasonNote: reason.lostReasonNote,
            userPicksFollowUp: req.userPicksFollowUp ? true : undefined,
        });
        const answer = new ReplaySubject<LeadStageChangeResult>(1);
        defer(() => this.taskLeadStageService.changeLeadStageFromTask(input))
            .pipe(
                defaultIfEmpty(null as ChangeLeadStageFromTaskResultDtoIHandleError | null),
                map((res) => toLeadStageChangeResult(res, taskId)),
                catchError(() => of(failedLeadStageChange(null))),
                takeUntil(this.destroy$)
            )
            .subscribe((result) => {
                this.announceStageChange(req, personId, result);
                answer.next(result);
                answer.complete();
            });
        return answer.asObservable();
    }

    /**
     * Configurable lost reasons: what a picked key sends - the tenant's reason by id (+ the Other note) while the list is loaded, MD's
     * code otherwise (a code the server maps to the same reason). Nothing for an unknown key.
     */
    lostReasonRequestOf(key: string | null | undefined, note?: string | null): LostReasonRequest {
        const catalog = this.lostReasons ? this.lostReasons.current() : null;
        if (catalog && findLostReason(catalog, key)) {
            return lostReasonRequest(catalog, key, note);
        }
        return isLostReasonCode(key) ? { lostReasonId: undefined, lostReason: key, lostReasonNote: undefined } : NO_LOST_REASON;
    }

    /**
     * Writes the lost reason picked in the task panel's Edit-relationship popover onto the transition its save just made
     * (FE-140; I.3): ONE `TaskLeadStage/SetLostReason` with `{ transitionId, lostReason }` (BE-67) — only when the
     * transition is a real move to a Lost-category stage with an id (`lostTransitionIdOf`) and the reason is one of the
     * six codes. Otherwise nothing is sent and it answers `{ sent: false, ok: true }`. Like `changeStage` it starts at
     * once, runs to its end even if the caller leaves, never errors and emits no notification: a refusal is `ok: false`
     * with the server's sentence, a transport failure or an empty answer `ok: false` with none.
     */
    setLostReason(
        transition: LostTransitionLike | null | undefined,
        lostReason: string | null | undefined
    ): Observable<LostReasonSaveResult> {
        const transitionId = lostTransitionIdOf(transition);
        if (transitionId === null || !isLostReasonCode(lostReason)) {
            return of<LostReasonSaveResult>({ sent: false, ok: true, errorMessage: null });
        }
        const input = new SetLostReasonInput({ transitionId, lostReason });
        const answer = new ReplaySubject<LostReasonSaveResult>(1);
        defer(() => this.taskLeadStageService.setLostReason(input))
            .pipe(
                defaultIfEmpty(null as TaskLeadStageAnswer | null),
                map((res: TaskLeadStageAnswer | null): LostReasonSaveResult => {
                    if (!res) {
                        return { sent: true, ok: false, errorMessage: null };
                    }
                    return res.isError
                        ? { sent: true, ok: false, errorMessage: textOrNull(res.errorMessage) }
                        : { sent: true, ok: true, errorMessage: null };
                }),
                catchError(() => of<LostReasonSaveResult>({ sent: true, ok: false, errorMessage: null })),
                takeUntil(this.destroy$)
            )
            .subscribe((result) => {
                answer.next(result);
                answer.complete();
            });
        return answer.asObservable();
    }

    /**
     * PR38: the Won popup's "Undo — keep Lead open" (Trovve.dc.html D5:5962) - ONE `TaskLeadStage/UndoWon` with
     * `{ transitionId }`; the server puts the stage, the type, the lead task and its follow-up back (BE, TaskLeadWonUndoer).
     * Like `changeStage` it starts at once, runs to its end even if the caller leaves and never errors: a refusal is
     * `ok: false` with the server's sentence, a transport failure or an empty answer `ok: false` with none. A success
     * announces the move back like any facade move - one `stageChanged$` (origin 'won_undo', when the person is back in
     * a stage) and one person-record `refreshContacts` - so every Stage surface of this tab re-reads that person.
     */
    undoWon(transitionId: number | null | undefined, wonStageId: number | null | undefined): Observable<WonUndoResult> {
        const id = idOrNull(transitionId);
        if (id === null) {
            return of(failedWonUndo(null));
        }
        const answer = new ReplaySubject<WonUndoResult>(1);
        defer(() => this.taskLeadStageService.undoWon(new UndoWonInput({ transitionId: id })))
            .pipe(
                defaultIfEmpty(null as UndoWonResultDtoIHandleError | null),
                map((res) => toWonUndoResult(res)),
                catchError(() => of(failedWonUndo(null))),
                takeUntil(this.destroy$)
            )
            .subscribe((result) => {
                this.announceWonUndo(result, idOrNull(wonStageId), id);
                answer.next(result);
                answer.complete();
            });
        return answer.asObservable();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.reload$.complete();
        this.stageChangedSource.complete();
        this.followUpRequestSource.complete();
    }

    /** After a real move: one `stageChanged$` and one person-record `refreshContacts` (no new channel). */
    private announceStageChange(req: ChangeLeadStageRequest, personId: number, result: LeadStageChangeResult): void {
        const transition = result.transition;
        if (!result.ok || !transition || transition.isTransition !== true) {
            return;
        }
        const movedPersonId = idOrNull(transition.clientId) || personId;
        const event: LeadStageChangedEvent = {
            personId: movedPersonId,
            fromStageId: idOrNull(transition.fromStageId) ?? idOrNull(req.fromStageId),
            toStageId: idOrNull(transition.toStageId) ?? req.toStageId,
            toCategory: leadStageCategoryOf(transition.toStageAct),
            origin: req.origin,
            contextTaskId: idOrNull(req.contextTaskId),
            transitionId: idOrNull(transition.transitionId),
            source: 'local',
        };
        this.stageChangedSource.next(event);
        this.dataService.refreshContacts.next({ kind: 'person', id: movedPersonId });
    }

    /** After an Undo: the person's move back, as `announceStageChange` announces a facade move. */
    private announceWonUndo(result: WonUndoResult, wonStageId: number | null, transitionId: number): void {
        if (!result.ok || result.personId === null) {
            return;
        }
        if (result.stageId !== null) {
            this.stageChangedSource.next({
                personId: result.personId,
                fromStageId: wonStageId,
                toStageId: result.stageId,
                toCategory: result.stageCategory,
                origin: 'won_undo',
                contextTaskId: null,
                transitionId,
                source: 'local',
            });
        }
        this.dataService.refreshContacts.next({ kind: 'person', id: result.personId });
    }

    /** One load; null on any failure, so the caller keeps its last good list. */
    private loadStages(): Observable<readonly LeadStageConfig[] | null> {
        return defer(() => {
            this.lastLoadStartedAtMs = Date.now();
            return this.contactService.getAllClientStages();
        }).pipe(
            map((result) => {
                const ok = !!result && !result.isError && Array.isArray(result.responseData);
                return ok ? toLeadStageConfigs(result.responseData) : null;
            }),
            catchError(() => of(null))
        );
    }
}

function sameStageConfigs(a: readonly LeadStageConfig[], b: readonly LeadStageConfig[]): boolean {
    return (
        a.length === b.length &&
        a.every((stage, i) => {
            const other = b[i];
            return (
                stage.id === other.id &&
                stage.name === other.name &&
                stage.category === other.category &&
                stage.sortOrder === other.sortOrder &&
                stage.isActive === other.isActive &&
                stage.suggestFollowUpDays === other.suggestFollowUpDays &&
                stage.nextStep === other.nextStep &&
                stage.nextStepNotes === other.nextStepNotes
            );
        })
    );
}

/** An Undo that did not happen: the server's sentence, or null for a transport failure. */
export function failedWonUndo(errorMessage: string | null): WonUndoResult {
    return {
        ok: false,
        errorMessage,
        personId: null,
        stageId: null,
        stageName: '',
        stageCategory: 'open',
        restoredTypeId: null,
        reopenedTaskId: null,
        followUpRestored: false,
        facts: null,
    };
}

/** `TaskLeadStage/UndoWon`'s answer -> `WonUndoResult`. No answer, or a success without its person, is a failure. */
export function toWonUndoResult(res: UndoWonResultDtoIHandleError | null | undefined): WonUndoResult {
    if (!res) {
        return failedWonUndo(null);
    }
    if (res.isError) {
        return failedWonUndo(textOrNull(res.errorMessage));
    }
    const data = res.responseData;
    const personId = data ? idOrNull(data.contactId) : null;
    if (!data || personId === null) {
        return failedWonUndo(null);
    }
    return {
        ok: true,
        errorMessage: null,
        personId,
        stageId: idOrNull(data.stageId),
        stageName: textOrNull(data.stageName) || '',
        stageCategory: leadStageCategoryOf(data.stageAct),
        restoredTypeId: data.typeRestored ? idOrNull(data.restoredContactTypeId) : null,
        reopenedTaskId: idOrNull(data.reopenedTaskId),
        followUpRestored: data.followUpRestored === true,
        facts: data.facts || null,
    };
}

/** The viewport box of an event's trigger (its `currentTarget`): where "Follow up on" opens. Null when it has none. */
export function triggerBoxOf(event: Event | null | undefined): LeadTriggerBox | null {
    const el = event && event.currentTarget instanceof Element ? event.currentTarget : null;
    if (!el) {
        return null;
    }
    const r = el.getBoundingClientRect();
    return { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
}

/** A positive whole id, else null. */
function idOrNull(value: number | null | undefined): number | null {
    return typeof value === 'number' && isFinite(value) && value > 0 ? value : null;
}

/** A non-blank string, trimmed, else null. */
function textOrNull(value: string | null | undefined): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}
